import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  MenuItem,
  Select,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";
import axios from "../../../config/axiosConfig";
import MuiAlert from "@mui/material/Alert";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";

// OT Desactivar Equipos Vendidos (tipo_ot=13): espejo de index.php/guarda.php de
// ot_desactivar_vendidos/ — a diferencia de AsignarVendidoOT.jsx (que cataloga equipo
// Vendido para reactivarlo), acá se listan los equipos "Vendido Activo" DE ESTE cliente
// (ya instalados) y se eligen cuáles dejan de estar operativos. Un solo paso: se arma la
// selección + observación por fila (y, para Chip suelto, el estado destino) y un único
// "Desactivar" manda la OT directo a Finalizado — igual que FinalizarMantenimientoOT.jsx,
// sin Fase 2 ni firma.
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.25, px: 0.6, fontSize: 11 } };

const ESTADOS_CHIP = [
  { value: "Inactivo", label: "Inactivo" },
  { value: "Baja", label: "Baja" },
  { value: "Mantenimiento", label: "Mantenimiento" },
];

const claveDe = (familia, identificador) => `${familia}-${identificador}`;

const DesactivarVendidosOT = ({ cod_ot, esTKR, esAVL, onFinalizado }) => {
  const [candidatos, setCandidatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tipo, setTipo] = useState("Radio");

  // { [clave]: { familia, identificador, modelo, observacion, estadoDestino } }
  const [seleccion, setSeleccion] = useState({});
  const [observacionGeneral, setObservacionGeneral] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [equipoDialog, setEquipoDialog] = useState(null);

  const cargar = () => {
    setLoading(true);
    setError("");
    axios
      .get(`/api/ot-desactivar-vendidos/candidatos/${cod_ot}`)
      .then((res) => setCandidatos(res.data || {}))
      .catch((err) => {
        console.error("Error al cargar equipos vendidos activos", err);
        setError("No se pudo cargar el equipo vendido activo de este cliente.");
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [cod_ot]);

  const toggleFila = (familia, identificador, modelo) => {
    const key = claveDe(familia, identificador);
    setSeleccion((prev) => {
      if (prev[key]) {
        const { [key]: _quitado, ...resto } = prev;
        return resto;
      }
      return {
        ...prev,
        [key]: {
          familia,
          identificador,
          modelo,
          observacion: "",
          estadoDestino: familia === "CHIP" ? "Inactivo" : undefined,
        },
      };
    });
  };

  const setCampoFila = (familia, identificador, campo, valor) => {
    const key = claveDe(familia, identificador);
    setSeleccion((prev) => ({ ...prev, [key]: { ...prev[key], [campo]: valor } }));
  };

  const seleccionados = Object.values(seleccion);

  const desactivar = async () => {
    if (!seleccionados.length) return;
    if (!window.confirm("¿Desactivar los equipos seleccionados? La OT quedará Finalizada.")) return;
    setEnviando(true);
    setError("");

    // Se abre la pestaña ya (en blanco) antes de cualquier await, en el mismo gesto de
    // click, para que el navegador no la bloquee como popup — igual que
    // FinalizarMantenimientoOT.jsx.
    const pdfWindow = window.open("", "_blank");
    if (pdfWindow) {
      pdfWindow.opener = null;
      pdfWindow.document.title = "Generando Nota de Desactivación...";
      pdfWindow.document.body.innerHTML =
        '<div style="font-family:Arial,sans-serif;padding:24px;color:#334155">Desactivando y generando la Nota de Desactivación...</div>';
    }

    try {
      const items = seleccionados.map(({ familia, identificador, observacion, estadoDestino }) => ({
        familia,
        identificador,
        observacion,
        ...(familia === "CHIP" ? { estadoDestino } : {}),
      }));
      const { data } = await axios.patch(`/api/ot-desactivar-vendidos/desactivar/${cod_ot}`, {
        items,
        observacion: observacionGeneral,
      });
      if (onFinalizado) onFinalizado(data);

      if (pdfWindow) {
        try {
          const { data: pdfData } = await axios.get(`/api/ot-solicitudes/${cod_ot}/nota-desactivacion.pdf`, { responseType: "blob" });
          const pdfUrl = URL.createObjectURL(new Blob([pdfData], { type: "application/pdf" }));
          pdfWindow.location.replace(pdfUrl);
          setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
        } catch {
          pdfWindow.close();
        }
      }
    } catch (err) {
      if (pdfWindow) pdfWindow.close();
      setError(err?.response?.data?.error || "No se pudo desactivar el equipo seleccionado.");
    } finally {
      setEnviando(false);
    }
  };

  const filaEquipo = (familia, row, sx) => {
    const key = claveDe(familia, row.identificador);
    const checked = Boolean(seleccion[key]);
    return (
      <TableRow key={key} hover selected={checked}>
        <TableCell sx={sx}>
          <Checkbox size="small" checked={checked} onChange={() => toggleFila(familia, row.identificador, row.modelo)} sx={{ p: 0 }} />
        </TableCell>
        <TableCell sx={sx}>
          <Box
            component="span"
            onClick={() => setEquipoDialog({ tipo: familia.toLowerCase(), identificador: row.identificador })}
            sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
          >
            {row.identificador}
          </Box>
        </TableCell>
        <TableCell sx={sx}>{row.modelo}</TableCell>
        {familia === "CHIP" && (
          <TableCell>
            <Select
              variant="standard"
              size="small"
              disabled={!checked}
              value={seleccion[key]?.estadoDestino || "Inactivo"}
              onChange={(e) => setCampoFila(familia, row.identificador, "estadoDestino", e.target.value)}
              sx={{ fontSize: 11, width: "100%" }}
            >
              {ESTADOS_CHIP.map((op) => (
                <MenuItem key={op.value} value={op.value} sx={{ fontSize: 11 }}>{op.label}</MenuItem>
              ))}
            </Select>
          </TableCell>
        )}
        <TableCell>
          <TextField
            variant="standard"
            size="small"
            disabled={!checked}
            value={seleccion[key]?.observacion || ""}
            onChange={(e) => setCampoFila(familia, row.identificador, "observacion", e.target.value)}
            fullWidth
            sx={{ "& .MuiInputBase-input": { fontSize: 10.5, py: 0 } }}
          />
        </TableCell>
      </TableRow>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  const radios = candidatos?.radios || [];
  const baterias = candidatos?.baterias || [];
  const modems = candidatos?.modems || [];
  const chipsSueltos = candidatos?.chipsSueltos || [];
  const varios = candidatos?.varios || [];

  return (
    <Box sx={{ mt: 1 }}>
      {error && (
        <MuiAlert severity="error" variant="outlined" sx={{ mb: 1.5, fontSize: 12 }}>
          {error}
        </MuiAlert>
      )}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Chip size="small" color="warning" variant="outlined" label={`Seleccionados: ${seleccionados.length}`} sx={{ height: 20, fontSize: 11 }} />
      </Stack>

      {esTKR && (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <ToggleButtonGroup size="small" exclusive color="primary" value={tipo} onChange={(e, nuevo) => nuevo && setTipo(nuevo)} sx={{ height: 30 }}>
              <ToggleButton value="Radio" sx={{ fontSize: 11.5, px: 1.5, py: 0.35, textTransform: "none" }}>
                Radios
              </ToggleButton>
              <ToggleButton value="Bateria" sx={{ fontSize: 11.5, px: 1.5, py: 0.35, textTransform: "none" }}>
                Baterías
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 360, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 32 }}>Sel.</TableCell>
                  <TableCell sx={headCellSx}>Serie</TableCell>
                  <TableCell sx={headCellSx}>Modelo</TableCell>
                  <TableCell sx={headCellSx}>Observación</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(tipo === "Radio" ? radios : baterias).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        Este cliente no tiene {tipo === "Radio" ? "radios" : "baterías"} vendidas activas.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (tipo === "Radio" ? radios : baterias).map((row) => filaEquipo(tipo === "Radio" ? "RADIO" : "BATERIA", row))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {esAVL && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
            Módems
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 360, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 32 }}>Sel.</TableCell>
                  <TableCell sx={headCellSx}>Serie Configurado</TableCell>
                  <TableCell sx={headCellSx}>Modelo</TableCell>
                  <TableCell sx={headCellSx}>Observación</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!modems.length ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        Este cliente no tiene módems vendidos activos.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  modems.flatMap((m) => [
                    filaEquipo("MODEM", m),
                    ...(m.chips || []).map((c) => filaEquipo("CHIP", c, { pl: 3 })),
                  ])
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
            Chips Sueltos
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 280, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 32 }}>Sel.</TableCell>
                  <TableCell sx={headCellSx}>Número</TableCell>
                  <TableCell sx={headCellSx}>Modelo</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 140 }}>Estado</TableCell>
                  <TableCell sx={headCellSx}>Observación</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!chipsSueltos.length ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        Este cliente no tiene chips sueltos activos.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  chipsSueltos.map((row) => filaEquipo("CHIP", row))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!esTKR && !esAVL && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 360, borderRadius: 0.5 }}>
          <Table size="small" stickyHeader sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, width: 32 }}>Sel.</TableCell>
                <TableCell sx={headCellSx}>Serie</TableCell>
                <TableCell sx={headCellSx}>Modelo</TableCell>
                <TableCell sx={headCellSx}>Observación</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!varios.length ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                      Este cliente no tiene equipos vendidos activos.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                varios.map((row) => filaEquipo("VARIOS", row))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TextField
        label="Observaciones Generales"
        fullWidth
        multiline
        rows={3}
        value={observacionGeneral}
        onChange={(e) => setObservacionGeneral(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="success"
          disabled={!seleccionados.length || enviando}
          onClick={desactivar}
        >
          {enviando ? <CircularProgress size={20} color="inherit" /> : "Desactivar"}
        </Button>
      </Box>

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
};

export default DesactivarVendidosOT;
